import { useCallback } from "react";
import axios from "axios";
import { toastError } from "../utils";
import { getDownloadService } from "../services/axios/services";

import { saveAs } from "file-saver";
import JSZip from "jszip";

function useDownloadAttachment() {
  const handleDownloadError = useCallback(
    () => toastError("Erro ao tentar fazer o download do arquivo."),
    []
  );

  const handleZipError = useCallback(
    () => toastError("Erro ao tentar fazer o download de todos os anexos."),
    []
  );

  const handleSaveFileFromBlob = ({ fileBlob, fileName }) => {
    const url = window.URL.createObjectURL(fileBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  function extractFileName(contentDisposition) {
    if (!contentDisposition) return "download";

    const match = contentDisposition.match(
      /filename\*?=(?:UTF-8'')?["\\]?([^";\\]+)["\\]?/i
    );

    if (match && match[1]) {
      return match[1].replace(/^\\+|\\+$/g, "").replace(/^"+|"+$/g, "");
    }

    return "download";
  }

  const downloadAttachment = useCallback(
    async ({ contentId, fileData, origin, mode, autoSave = true, cancelToken }) => {
      const fileId = fileData.id;
      const endpoint = `/Attachments/${fileId}/download`;
      const queryParams = { origin, mode, attachmentId: fileId };
      const requestConfig = { responseType: "blob", cancelToken };

      return getDownloadService({
        endpoint,
        params: queryParams,
        options: requestConfig,
        api: "v2",
      })
        .then((response) => {
          const fileBlob = response.data;
          const foundContentDispositionHeaders =
            response?.headers[`content-disposition`];

          const fileName = extractFileName(foundContentDispositionHeaders);

          if (autoSave) return handleSaveFileFromBlob({ fileBlob, fileName });

          return {
            blob: fileBlob,
            fileName,
          };
        })
        .catch((error) => {
          // lets the caller abort a batch (e.g. zip) without an error toast
          if (axios.isCancel(error)) throw error;
          handleDownloadError();
        });
    },
    [handleDownloadError]
  );

  const downloadSharedAttachment = useCallback(
    async ({ sharingId, origin, mode, autoSave = true }) => {
      const endpoint = `Sharing/${sharingId}/attachment/download`;
      const queryParams = { origin, mode };
      const requestConfig = { responseType: "blob" };

      return getDownloadService({
        endpoint,
        params: queryParams,
        options: requestConfig,
      })
        .then((response) => {
          const fileName = extractFileName(
            response.headers[`content-disposition`]
          );

          if (autoSave)
            return handleSaveFileFromBlob({
              fileBlob: response.data,
              fileName,
            });

          return {
            blob: response.data,
            fileName,
          };
        })
        .catch(() => {
          handleDownloadError();
        });
    },
    [handleDownloadError]
  );

  const downloadAndZipMultipleAttachments = useCallback(
    async ({
      filesArray,
      contentId,
      origin,
      mode,
      title,
      setLoading,
      callback,
      cancelToken,
    }) => {
      setLoading(true);

      try {
        const requests = filesArray.map((fileData) => {
          return downloadAttachment({
            contentId,
            fileData,
            origin,
            mode,
            autoSave: false,
            cancelToken,
          });
        });

        const results = await Promise.all(requests);

        const zip = new JSZip();
        const zipFileName = `${title}.zip`;
        const nameCount = {};

        results.forEach(({ blob, fileName }) => {
          const splitStringOnDots = (string) => string.split(".");
          const splitedFileName = splitStringOnDots(fileName);
          const extension = splitedFileName.length - 1;

          if (nameCount[fileName]) nameCount[fileName]++;
          if (!nameCount[fileName]) nameCount[fileName] = 1;

          zip.file(
            `${splitedFileName[0]} (${nameCount[fileName]}).${splitedFileName[extension]}`,
            blob,
            { binary: true }
          );
        });

        const zipBlob = await zip.generateAsync({ type: "blob" });
        saveAs(zipBlob, zipFileName);

        setLoading(false);

        if (callback) callback();
      } catch (error) {
        if (!axios.isCancel(error)) handleZipError();
        setLoading(false);
      }
    },
    [downloadAttachment, handleZipError]
  );

  return {
    downloadAttachment,
    downloadAndZipMultipleAttachments,
    downloadSharedAttachment,
  };
}

export default useDownloadAttachment;
